<template>
  <div>
    <PageHeader
      :links="[
        { url: '/', label: 'Home' },
        { url: '/legal/terms', label: 'Terms & Conditions' },
        { url: '/docs', label: 'Docs' },
      ]"
    />
    <main>
      <PageBlock>
        <div class="head">
          <h1>Privacy Policy</h1>
        </div>

        <div class="content">
          <p class="last-updated">Last updated April 4th, 2024</p>

          <h2>General Information</h2>
          <p>
            Devanco, Inc. (d/b/a “Otago”) is committed to protecting the privacy and security of the information we
            collect and to being transparent about the ways in which we collect and process your information. This
            statement (the “Privacy Policy”) sets forth our policies and practices for handling the information we
            collect from or about you. It applies to the websites and services that we operate and that link to this
            Privacy Policy (the “Services”).
          </p>

          <h2>Collection of Information</h2>
          <p>We collect the following categories of information when you use our Services:</p>
          <ul>
            <li>
              Information you provide directly to us:
              <ul>
                <li>Sign-In Information, including your username, password, and email address.</li>
                <li>
                  Transaction and Billing Data, including your bank account and payment card details (credit card
                  number, expiration date, and credit card security code, where needed to complete a transaction) and
                  billing address. All payment data is stored by our payment processor.
                </li>
                <li>
                  Commercial information, including records of products or services purchased, obtained, or considered,
                  or other purchasing or consuming histories or tendencies.
                </li>
                <li>
                  When end-users use apps made with Otago, we may collect anonymous device identifiers, if you are
                  utilizing Otago’s code push service.
                </li>
                <li>Correspondence you send to us, such as emails.</li>
              </ul>
            </li>
            <li>
              <p>
                Information we collect automatically: We collect Internet, or other electronic network activity
                information automatically from the devices and browsers that you use, including your IP address, browser
                type and operating system (including mobile device information, if applicable), errors, performance
                metrics, and webpage and app interactions.
              </p>
            </li>
          </ul>
          <p>
            We also receive the categories of information described above from other sources, including from users of
            our Services and third-party services and organizations. Without this information, we are not able to
            provide you with all the requested services, and any differences in services are related to your
            information. We may aggregate or de-identify the information described above. Aggregated or de-identified
            data is not subject to this Privacy Policy.
          </p>

          <h2>Disclosure of Information</h2>
          <p>
            We are committed to maintaining your trust, and we want you to understand when and with whom we may share
            the information we collect.
          </p>
          <ul>
            <li>
              Authorized third-party vendors and service providers: We send the information you provide directly to us,
              the information we collect automatically, and the information we collect from other sources to services
              that process data for us for business purposes related to providing products or services you’ve requested
              (such as payment processing, analytics, error logging, customer interactions, and data processing and
              storage).
            </li>
            <li>
              Legal purposes: We may disclose any of the information we collect to respond to subpoenas, court orders,
              legal process, law enforcement requests, legal claims or government inquiries, and to protect and defend
              the rights, interests, safety, and security of Otago, our affiliates, users, or the public (including
              responding to illegal activities, suspected fraud, situations involving potential threats to the physical
              safety of any person, or violations of our Terms of Service).
            </li>
          </ul>
          <p>
            Consent: We may share any of the information we collect for any other purposes disclosed to you at the time
            we collect the information or pursuant to your consent.
          </p>
          <p>
            Otago does not sell your information to third parties. Otago does permit third parties to collect the
            information described above through our Service and discloses such information with third parties for
            business purposes as described in this Privacy Policy. The information practices of these third parties are
            not covered by this Privacy Policy.
          </p>

          <h2>Cookies</h2>
          <p>
            Where required by law, we will present you with a cookie banner the first time you visit our website that
            explains what type of cookies we use and which asks your consent for some of the cookies. You may be able to
            refuse or disable cookies by adjusting your web browser settings. Some browsers have options that allow the
            visitor to control whether the browser will accept cookies, reject cookies, or notify the visitor each time
            a cookie is sent. Because each web browser is different, please consult the instructions provided by your
            web browser (typically in the “help” section).
          </p>
          <p>
            If you choose to refuse, disable, or delete these technologies, some of the functionality of the Services
            may no longer be available to you and any differences in service are related to the data. Deleting cookies
            may in some cases cancel the opt-out selection in your browser.
          </p>

          <h2>International Transfers</h2>
          <p>
            We may transfer and store your information on servers located outside your resident jurisdiction. To the
            extent you are a resident of a country other than the United States, you consent to the transfer of such
            data to the United States for processing by us in accordance with this Privacy Policy.
          </p>
          <p>
            We do not knowingly collect or sell any information from children, as defined by applicable law, without
            parental consent or as otherwise permitted by applicable law. The website is not directed at children.
          </p>

          <h2>Use of Information and Purpose of Data Processing</h2>
          <p>
            Information will be retained only for so long as reasonably necessary for the purposes set out above, in
            accordance with applicable laws. We maintain reasonable security measures to safeguard information from
            loss, theft interference, misuse, unauthorized access, disclosure, alteration, or destruction. We also
            maintain reasonable procedures to help ensure that such data is reliable for its intended use and is
            accurate, complete, and current.
          </p>
          <p>
            You should understand that no data storage system or transmission of data over the Internet or any other
            public network can be guaranteed to be 100 percent secure, accurate, complete, or current. Please note that
            information collected by third parties may not have the same security protections as information you submit
            to us, and we are not responsible for protecting the security of such information. You can see a list of
            services we use below.
          </p>

          <h2>Data Retention, Security, and Integrity</h2>
          <p>
            Information will be retained only for so long as reasonably necessary for the purposes set out above, in
            accordance with applicable laws.
          </p>
          <p>
            We maintain reasonable security measures to safeguard information from loss, theft interference, misuse,
            unauthorized access, disclosure, alteration, or destruction. We also maintain reasonable procedures to help
            ensure that such data is reliable for its intended use and is accurate, complete, and current. You should
            understand that no data storage system or transmission of data over the Internet or any other public network
            can be guaranteed to be 100 percent secure, accurate, complete, or current. Please note that information
            collected by third parties may not have the same security protections as information you submit to us, and
            we are not responsible for protecting the security of such information.
          </p>

          <h2>Changes to the Privacy Policy</h2>
          <p>
            Otago may periodically update this policy. When we update the Privacy Policy, we will indicate the “revised
            date” above and post the new Privacy Policy. We recommend that you review the Privacy Policy each time you
            visit the Services to stay informed of our privacy practices.
          </p>

          <h2>Questions</h2>
          <p>
            Any questions about this Privacy Policy or our practices should be sent to us via email at
            <a href="mailto:privacy@otago.dev">privacy@otago.dev</a>.
          </p>
        </div>
      </PageBlock>
    </main>
    <PageFooter />
  </div>
</template>

<script setup lang="ts">
useHead({
  title: "Otago - Privacy Policy",
});
</script>

<style scoped>
.head {
  @apply py-4 lg:py-8 px-4 mx-auto max-w-screen-xl text-center;

  h1 {
    @apply mb-4 text-3xl font-extrabold tracking-tight leading-none md:text-5xl dark:text-blue-400;
  }
}

.content {
  @apply pt-6 pb-10 max-w-screen-md mx-auto text-white tracking-wide leading-relaxed;

  h2 {
    @apply mt-8 mb-4 text-2xl font-semibold dark:text-blue-400;
  }

  p {
    @apply my-3;
  }

  ul {
    @apply list-disc list-outside pl-5;

    li {
      @apply my-3;
    }
  }

  .last-updated {
    @apply italic mb-6;
  }
}
</style>
